import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { FormattedHeading } from '../../../i18n/components/';
import * as Util from '../../../common/utilities/';

import {
  AssetArchiving,
  ContingencyImageCard,
  DeactivateWeatherModal,
  SubscriberType,
  Tagging,
  Timezone,
  Weather
} from '../';

import {
  Button,
  Input,
  Popover,
  Tooltip
} from '@salesforce/design-system-react';

import './SetupContainer.scss';

const POLL_WEATHER_DELETE_MS = 5000;

class SetupContainer extends Component {
  componentDidMount() {
    this.props.actions.getAssetTypes();
    this.props.actions.getAttributes();
    this.props.actions.searchAssets(
      { has_weather: 'true' },
      { page: 1, size: 1 },
      { context: 'setup', mode: 'reset' }
    );
  }

  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'rules.settings.weather_delete_in_progress'
      )
    ) {
      if (this.isWeatherDeleteInProgress()) {
        this.polling = setInterval(
          () => this.props.actions.getRules(),
          POLL_WEATHER_DELETE_MS
        );
      } else if (this.polling) {
        clearInterval(this.polling);
        this.props.actions.getAssetTypes();
        this.props.actions.getAttributes();
        this.props.actions.searchAssets(
          { has_weather: 'true' },
          { page: 1, size: 1 },
          { context: 'setup', mode: 'reset' }
        );
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.polling);
  }

  settingChangeHandler(setting, value, saveEmptyValues = false) {
    if (value || saveEmptyValues) {
      const rules = {
        ...this.props.rules,
        [setting]: value
      };
      this.props.actions.saveRules(rules, {
        ruleType: 'setup',
        cToken: this.props.session.cToken
      });
    }
  }

  updateContingencyLink(event, { value }) {
    this.props.actions.updateRules({
      ...this.props.rules,
      contingency_link: value
    });
  }

  canSaveContingencyLink() {
    const loaded = this.props.loadedRules.contingency_link || '';
    const current = this.props.rules.contingency_link || '';
    return loaded !== current;
  }

  saveContingencyLink() {
    this.props.actions.saveContingencyLink(this.props.rules.contingency_link, {
      cToken: this.props.session.cToken
    });
  }

  renderTooltip(eventType) {
    const { formatMessage } = this.props.intl;
    return (
      <Tooltip
        align="top left"
        content={formatMessage({
          id: `components.ecs.rules.contingencyLink.tooltip`
        })}
        postition="absolute"
      />
    );
  }

  state = {
    archivePopupIsOpen: false,
    conversionPopupIsOpen: false,
    archivePeriodChanged: false,
    taggingPopupIsOpen: false,
    relaxPopupIsOpen: false,
    timezonePopupIsOpen: false
  };

  isConversion() {
    return this.props.rules?.settings?.conversion;
  }

  isRelax() {
    if (this.props.rules?.settings?.relax_fatigue === undefined) {
      return true;
    } else {
      return this.props.rules?.settings?.relax_fatigue;
    }
  }

  popOverColor(field) {
    if (field) {
      return 'destructive';
    } else {
      return 'brand';
    }
  }

  buttonColor(field) {
    if (field) {
      return 'brand';
    } else {
      return 'neutral';
    }
  }

  isTagging() {
    return this.props.rules?.settings?.ect;
  }

  isWeather() {
    return !!this.props.rules?.settings?.weather;
  }

  isWeatherDeleteInProgress() {
    return !!this.props.rules?.settings?.weather_delete_in_progress;
  }

  buttonTextColor(isEnabled) {
    if (isEnabled) {
      return 'deactivate';
    }
  }

  handlePopupOpen(fieldState) {
    this.setState({ [fieldState]: true });
  }

  handlePopupCancel(fieldState) {
    this.setState({ [fieldState]: false });
  }

  handleRequestClose(fieldState) {
    if (this.props.log) {
      this.props.log('onRequestClose');
    }
    this.setState({ [fieldState]: false });
  }

  handleClose = (event, data) => {
    if (this.props.log) {
      this.props.log('onClose')(event, data);
    }
  };

  // Only open Popup when deactivating
  handleTaggingOpen = () => {
    if (this.isTagging()) {
      this.setState({ taggingPopupIsOpen: true });
    } else {
      this.handleTaggingChange();
    }
  };

  handleSettingsUpdate = () => {
    this.props.actions.saveRules(this.props.rules, {
      ruleType: 'settings',
      cToken: this.props.session.cToken
    });
  };

  handleApply = () => {
    const rules = {
      ...this.props.rules,
      settings: { conversion: !this.isConversion() }
    };
    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      cToken: this.props.session.cToken
    });
    this.setState({ conversionPopupIsOpen: false });
  };

  handleTaggingApply = () => {
    const rules = {
      ...this.props.rules,
      settings: { ect: false }
    };
    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      cToken: this.props.session.cToken
    });
    this.setState({ taggingPopupIsOpen: false });
  };

  handleRelaxApply = () => {
    const rules = {
      ...this.props.rules,
      settings: { relax_fatigue: !this.isRelax() }
    };
    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      cToken: this.props.session.cToken
    });
    this.setState({ relaxPopupIsOpen: false });
  };

  archiveDropdownHandler(hasChanged, period) {
    if (hasChanged) {
      this.setState({
        archivePeriodChanged: true,
        tempArchivePeriod: period
      });
    } else {
      this.setState({
        archivePeriodChanged: false
      });
    }
  }

  handleArchiveChange = () => {
    let rules = {
      ...this.props.rules,
      settings: { archive_after_days: this.state.tempArchivePeriod }
    };

    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      callback: () => {
        this.setState({
          archivePopupIsOpen: false,
          archivePeriodChanged: false
        });
      },
      cToken: this.props.session.cToken
    });
  };

  handleTaggingChange = () => {
    const { ect_min_confidence_level: confidence, ect_max_tags: tags } =
      this.props.rules?.settings || {};

    const rules = {
      ...this.props.rules,
      settings: {
        ect: true,
        ect_min_confidence_level: confidence || 0.6,
        ect_max_tags: tags || 10
      }
    };

    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      callback: () => {
        this.setState({
          taggingPopupIsOpen: false
        });
      },
      cToken: this.props.session.cToken
    });
  };

  handleTaggingSettingChange(setting, value) {
    let parsedValue = Number.parseFloat(value);
    if (setting === 'ect_min_confidence_level') {
      parsedValue = parsedValue / 100;
    }
    const updated = {
      ...this.props.rules,
      settings: {
        ...(this.props.rules?.settings || {}),
        [setting]: parsedValue
      }
    };
    this.props.actions.updateRules(updated);
  }

  timezoneDropdownHandler(hasChanged, timezone) {
    if (hasChanged) {
      this.setState({
        timezoneChanged: true,
        tempTimezone: timezone
      });
    } else {
      this.setState({
        timezoneChanged: false
      });
    }
  }

  handleTimezoneChange = () => {
    let rules = {
      ...this.props.rules,
      settings: { time_zone: this.state.tempTimezone }
    };

    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      callback: () => {
        this.setState({
          timezonePopupIsOpen: false,
          timezoneChanged: false
        });
      },
      cToken: this.props.session.cToken
    });
  };

  handleWeatherChange = (currentWeather, fromModal) => {
    if (currentWeather && !fromModal) {
      this.props.actions.openModal('deactivateWeather');
      return;
    }

    // default the weather_degree to F
    const rules = {
      ...this.props.rules,
      settings: { weather: !currentWeather, weather_degree: 'F' }
    };
    this.props.actions.saveRules(rules, {
      ruleType: 'settings',
      cToken: this.props.session.cToken
    });
    this.props.actions.closeModal('deactivateWeather');
  };

  handleWeatherSettingChange(setting, value) {
    const updated = {
      ...this.props.rules,
      settings: {
        ...(this.props.rules?.settings || {}),
        [setting]: value
      }
    };
    this.props.actions.updateRules(updated);
  }

  renderSubscriberType() {
    const { formatMessage } = this.props.intl;
    return (
      <div className="setup-panel">
        <FormattedHeading id="components.ecs.rules.subscriber.heading" />
        <p>
          {formatMessage({
            id: 'components.ecs.rules.subscriber.description'
          })}
        </p>
        <SubscriberType
          onSettingChanged={this.settingChangeHandler.bind(this)}
          subscriberType={this.props.rules.subscriber_type}
        />
      </div>
    );
  }

  renderConversion() {
    const { formatMessage } = this.props.intl;
    const isTracking = !!this.isConversion();
    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.conversion.heading" />
            <p className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.conversion.description'
              })}
            </p>
          </div>
          <div className="right-nav slds-col">
            <Popover
              className="slds-text-align_left"
              isOpen={this.state.conversionPopupIsOpen}
              body={formatMessage({
                id: 'components.ecs.rules.conversion.confirmation.description'
              })}
              footer={
                <div className="slds-text-align_right">
                  <Button
                    label="Cancel"
                    onClick={() =>
                      this.handlePopupCancel('conversionPopupIsOpen')
                    }
                  />
                  <Button
                    variant={this.popOverColor(this.isConversion())}
                    label={formatMessage({
                      id: `components.ecs.rules.conversion.confirmation.confirm.${!isTracking}`
                    })}
                    onClick={this.handleApply}
                  />
                </div>
              }
              heading={formatMessage({
                id: `components.ecs.rules.conversion.confirmation.header.${!isTracking}`
              })}
              id="popover-controlled-with-footer"
              onClose={e => {
                this.handleClose(e);
              }}
              onRequestClose={() =>
                this.handleRequestClose('conversionPopupIsOpen')
              }
            >
              <Button
                className="conversion-button"
                id="conversion-tracking"
                key="conversion"
                variant={this.buttonColor(this.isConversion())}
                onClick={() => this.handlePopupOpen('conversionPopupIsOpen')}
                label={formatMessage({
                  id: `components.ecs.rules.conversion.tracking.${isTracking}`
                })}
              />
            </Popover>
          </div>
        </div>
      </div>
    );
  }

  renderArchive() {
    const { formatMessage } = this.props.intl;
    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.archiving.heading" />
            <p className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.archiving.description'
              })}
            </p>
            <AssetArchiving
              onSettingChanged={this.archiveDropdownHandler.bind(this)}
              tempArchivePeriod={this.state.tempArchivePeriod}
              archivePeriod={
                //if archive_after_days is undefined then set it to -1 to make sure that Never is selected by default
                this.props.rules.settings?.archive_after_days || -1
              }
              periodChanged={this.state.archivePeriodChanged}
            />
          </div>
          <div className="right-nav slds-col">
            <Popover
              className="slds-text-align_left"
              isOpen={this.state.archivePopupIsOpen}
              body={formatMessage({
                id: 'components.ecs.rules.archiving.confirmation.description'
              })}
              footer={
                <div className="slds-text-align_right">
                  <Button
                    label="Cancel"
                    onClick={() => this.handlePopupCancel('archivePopupIsOpen')}
                  />
                  <Button
                    variant={this.popOverColor(this.isConversion())}
                    label={formatMessage({
                      id: `components.ecs.rules.archiving.confirmation.confirm`
                    })}
                    onClick={this.handleArchiveChange}
                  />
                </div>
              }
              heading={formatMessage({
                id: `components.ecs.rules.archiving.confirmation.header`
              })}
              id="popover-controlled-with-footer"
              onClose={this.handleClose}
              onRequestClose={() =>
                this.handleRequestClose('archivePopupIsOpen')
              }
            >
              <Button
                className="archive-button"
                id="archiving"
                key="archive"
                disabled={!this.state.archivePeriodChanged}
                variant={this.buttonColor(this.state.archivePeriodChanged)}
                onClick={() => this.handlePopupOpen('archivePopupIsOpen')}
                label={formatMessage({
                  id: `components.ecs.rules.archiving.update`
                })}
              />
            </Popover>
          </div>
          <div />
        </div>
      </div>
    );
  }

  renderRelax() {
    const { formatMessage } = this.props.intl;
    const isRelax = !!this.isRelax();
    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.relax.heading" />
            <span className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.relax.description'
              })}
              <Tooltip
                align="right"
                triggerClassName="relax-tooltip"
                content={formatMessage({
                  id: `components.ecs.rules.relax.tooltip`
                })}
                postition="absolute"
              />
            </span>
          </div>
          <div className="right-nav slds-col">
            <Popover
              className="slds-text-align_left"
              isOpen={this.state.relaxPopupIsOpen}
              body={formatMessage({
                id: 'components.ecs.rules.relax.confirmation.description'
              })}
              footer={
                <div className="slds-text-align_right">
                  <Button
                    label="Cancel"
                    onClick={() => this.handlePopupCancel('relaxPopupIsOpen')}
                  />
                  <Button
                    variant={this.popOverColor(this.isRelax())}
                    label={formatMessage({
                      id: `components.ecs.rules.relax.confirmation.confirm.${!isRelax}`
                    })}
                    onClick={this.handleRelaxApply}
                  />
                </div>
              }
              heading={formatMessage({
                id: `components.ecs.rules.relax.confirmation.header.${!isRelax}`
              })}
              id="popover-controlled-with-footer"
              onClose={e => {
                this.handleClose(e);
              }}
              onRequestClose={() => this.handleRequestClose('relaxPopupIsOpen')}
            >
              <Button
                className="relax-button"
                id="relax-fatigue"
                key="relax"
                variant={this.buttonColor(this.isRelax())}
                onClick={() => this.handlePopupOpen('relaxPopupIsOpen')}
                label={formatMessage({
                  id: `components.ecs.rules.relax.fatigue.${!!isRelax}`
                })}
              />
            </Popover>
          </div>
        </div>
      </div>
    );
  }

  renderTimezone() {
    const { formatMessage } = this.props.intl;
    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.timezone.heading" />
            <p className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.timezone.description'
              })}
            </p>
            <Timezone
              onSettingChanged={this.timezoneDropdownHandler.bind(this)}
              tempTimezone={this.state.tempTimezone}
              timezone={this.props.rules.settings?.time_zone || 'UTC'}
              timezoneChanged={this.state.timezoneChanged}
            />
          </div>
          <div className="right-nav slds-col">
            <Popover
              className="slds-text-align_left"
              isOpen={this.state.timezonePopupIsOpen}
              body={formatMessage({
                id: 'components.ecs.rules.timezone.confirmation.description'
              })}
              footer={
                <div className="slds-text-align_right">
                  <Button
                    label="Cancel"
                    onClick={() =>
                      this.handlePopupCancel('timezonePopupIsOpen')
                    }
                  />
                  <Button
                    variant={this.popOverColor(this.isConversion())}
                    label={formatMessage({
                      id: `components.ecs.rules.timezone.confirmation.confirm`
                    })}
                    onClick={this.handleTimezoneChange}
                  />
                </div>
              }
              heading={formatMessage({
                id: `components.ecs.rules.timezone.confirmation.header`
              })}
              id="popover-controlled-with-footer"
              onClose={this.handleClose}
              onRequestClose={() =>
                this.handleRequestClose('timezonePopupIsOpen')
              }
            >
              <Button
                className="timezone-button"
                id="time_zone"
                key="timezone"
                disabled={!this.state.timezoneChanged}
                variant={this.buttonColor(this.state.timezoneChanged)}
                onClick={() => this.handlePopupOpen('timezonePopupIsOpen')}
                label={formatMessage({
                  id: `components.ecs.rules.timezone.update`
                })}
              />
            </Popover>
          </div>
          <div />
        </div>
      </div>
    );
  }

  renderContigency() {
    const { formatMessage } = this.props.intl;
    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <FormattedHeading id="components.ecs.rules.contingencyImage.heading" />
        <p>
          {formatMessage({
            id: 'components.ecs.rules.contingencyImage.description'
          })}
        </p>
        <ContingencyImageCard
          actions={this.props.actions}
          contingencyImage={this.props.rules.contingency_image}
          session={this.props.session}
        />
        <div className="contingency-link">
          <Input
            className="contingency-link-input"
            fieldLevelHelpTooltip={this.renderTooltip('contingency_link')}
            label={formatMessage({
              id: 'components.ecs.rules.contingencyLink.heading'
            })}
            value={this.props.rules.contingency_link || ''}
            onChange={this.updateContingencyLink.bind(this)}
          />
          <Button
            className="save-contingency-link"
            label={formatMessage({
              id: 'components.ecs.rules.contingencyLink.button'
            })}
            disabled={!this.canSaveContingencyLink()}
            variant={this.buttonColor(this.canSaveContingencyLink())}
            onClick={this.saveContingencyLink.bind(this)}
          />
        </div>
      </div>
    );
  }

  renderTagging() {
    const { formatMessage } = this.props.intl;
    const isTagging = !!this.isTagging();
    const settingsChanged =
      isTagging &&
      (JSON.stringify(this.props.rules.settings?.ect_min_confidence_level) !==
        JSON.stringify(
          this.props.loadedRules.settings?.ect_min_confidence_level
        ) ||
        JSON.stringify(this.props.rules.settings?.ect_max_tags) !==
          JSON.stringify(this.props.loadedRules.settings?.ect_max_tags));

    const updateButton = isTagging ? (
      <Button
        id="einstein-tagging-update"
        key="tagging-update"
        variant={this.buttonColor(settingsChanged)}
        disabled={!settingsChanged}
        onClick={() => this.handleSettingsUpdate()}
        label={formatMessage({
          id: 'components.ecs.rules.tagging.button.update'
        })}
      />
    ) : null;

    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.tagging.heading" />
            <p className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.tagging.description'
              })}
            </p>
            <Tagging
              isTagging={this.isTagging()}
              ect_min_confidence_level={
                this.props.rules.settings?.ect_min_confidence_level
              }
              ect_max_tags={this.props.rules.settings?.ect_max_tags}
              onSettingChanged={this.handleTaggingSettingChange.bind(this)}
            />
          </div>
          <div className="right-nav slds-col">
            <Popover
              className="slds-text-align_left"
              isOpen={this.state.taggingPopupIsOpen}
              body={formatMessage({
                id: 'components.ecs.rules.tagging.confirmation.description'
              })}
              footer={
                <div className="slds-text-align_right">
                  <Button
                    label="Cancel"
                    onClick={() => this.handlePopupCancel('taggingPopupIsOpen')}
                  />
                  <Button
                    id="deactivate-tagging"
                    variant="destructive"
                    label={formatMessage({
                      id:
                        'components.ecs.rules.tagging.confirmation.confirm.false'
                    })}
                    onClick={this.handleTaggingApply}
                  />
                </div>
              }
              heading={formatMessage({
                id: 'components.ecs.rules.tagging.confirmation.header.false'
              })}
              id="popover-controlled-with-footer"
              onClose={this.handleClose}
              onRequestClose={() =>
                this.handleRequestClose('taggingPopupIsOpen')
              }
            >
              <div className="vertical-buttons">
                <Button
                  className={this.buttonTextColor(isTagging)}
                  id="einstein-tagging"
                  key="tagging"
                  variant="neutral"
                  onClick={this.handleTaggingOpen}
                  label={formatMessage({
                    id: `components.ecs.rules.tagging.button.${isTagging}`
                  })}
                />
                {updateButton}
              </div>
            </Popover>
          </div>
        </div>
      </div>
    );
  }

  renderWeather() {
    const { formatMessage } = this.props.intl;
    const isWeather = this.isWeather();
    const weatherChanged =
      isWeather &&
      JSON.stringify(this.props.rules.settings?.weather_degree) !==
        JSON.stringify(this.props.loadedRules.settings?.weather_degree);

    const updateWeatherButton = isWeather ? (
      <Button
        id="einstein-weather-update"
        key="weather-update"
        variant={this.buttonColor(weatherChanged)}
        disabled={!weatherChanged}
        onClick={() => this.handleSettingsUpdate()}
        label={formatMessage({
          id: 'components.ecs.rules.weather.button.update'
        })}
      />
    ) : null;

    return (
      <div className="setup-panel">
        <div className="horizontal-rule" />
        <div className="slds-grid">
          <div className="step-info slds-col">
            <FormattedHeading id="components.ecs.rules.weather.heading" />
            <p className="step-description">
              {formatMessage({
                id: 'components.ecs.rules.weather.description'
              })}
            </p>
            <Weather
              isWeather={isWeather}
              isWeatherDeleting={this.isWeatherDeleteInProgress()}
              weatherDegree={this.props.rules.settings?.weather_degree}
              onSettingChanged={this.handleWeatherSettingChange.bind(this)}
            />
          </div>
          <div className="right-nav slds-col">
            <div className="vertical-buttons">
              <Button
                className={this.buttonTextColor(isWeather)}
                id="einstein-weather"
                key="weather"
                variant="neutral"
                onClick={() => this.handleWeatherChange(isWeather)}
                disabled={this.isWeatherDeleteInProgress()}
                label={formatMessage({
                  id: `components.ecs.rules.weather.button.${isWeather}`
                })}
              />
              {updateWeatherButton}
            </div>
          </div>
        </div>
      </div>
    );
  }

  deactivateWeather() {
    this.handleWeatherChange(true, true);
  }

  deactivateWeatherProps() {
    const weatherAssetClasses =
      this.props.assets.data.types?.filter(type => type.weather_aware) || [];
    const weatherAttributes =
      this.props.attributes.data.weatherAttributes || [];
    const weatherAssetCount =
      this.props.assets.data.search?.setup?.paging?.total || 0;
    return { weatherAssetClasses, weatherAttributes, weatherAssetCount };
  }

  render() {
    return (
      <div className="slds-grid slds-wrap setup-controls">
        {this.renderSubscriberType()}
        {this.renderTimezone()}
        {this.renderConversion()}
        {this.renderArchive()}
        {this.renderTagging()}
        {this.props?.flags?.dontRelaxFatigue ? this.renderRelax() : null}
        {this.renderWeather()}
        {this.renderContigency()}
        <DeactivateWeatherModal
          actions={this.props.actions}
          modals={this.props.modals}
          onSubmit={() => this.deactivateWeather()}
          {...this.deactivateWeatherProps()}
        />
      </div>
    );
  }
}

SetupContainer.propTypes = {
  actions: PropTypes.shape({
    saveRules: PropTypes.func,
    getAssetTypes: PropTypes.func,
    getAttributes: PropTypes.func,
    searchAssets: PropTypes.func
  }),
  assets: PropTypes.object,
  attributes: PropTypes.object,
  modals: PropTypes.object,
  flags: PropTypes.object.isRequired,
  rules: PropTypes.object,
  loadedRules: PropTypes.object,
  location: PropTypes.object,
  session: PropTypes.object
};

export default injectIntl(SetupContainer);
